import DOMPurify from "dompurify";
import React, { ReactElement } from "react";
import { jsx } from "slate-hyperscript";

import {
    BlockHandler, ElementType, StateElement, TextElement,
} from "./base";
import Styles from "../Editor.module.scss";
import {
    ALIGN_CLASS, Align, BORDER_CLASS, MediaSettings, MediaToolbar, SHADOW_CLASS,
} from "./media-toolbar";

export interface ImageSettings extends MediaSettings {
    src?: string;
    altText?: string;
}

BlockHandler.register(ElementType.IMAGE, {
    nodeTypes: ["IMG"],
    toBlock: (child: ReactElement, settings: ImageSettings, editorSettings) => {
        const setValue = (value: MediaSettings) => {
            editorSettings.onUpdate({
                type: ElementType.IMAGE,
                [ElementType.IMAGE]: {
                    ...settings,
                    ...value,
                },
                children: [{
                    text: "",
                } as TextElement],
            } as StateElement);
        };

        const styles: {width?: number; height?: number} = {};
        if (settings.width) {
            styles.width = typeof settings.width === "string" ? parseInt(settings.width, 10) : settings.width;
        }
        if (settings.height) {
            styles.height = typeof settings.height === "string" ? parseInt(settings.height, 10) : settings.width;
        }

        return (
            <div
                contentEditable={false}
                className={`position-relative d-block ${Styles["editable-image"]} ${ALIGN_CLASS[settings.align ?? Align.LEFT]}`}
                style={styles}
            >
                {!editorSettings.isReadOnly && (
                    <MediaToolbar settings={settings} onSetValue={setValue} onRemove={editorSettings.onRemove} />
                )}
                <img
                    src={settings.src}
                    alt={settings.altText}
                    className={`w-100 h-100 ${
                        settings.shadow ? SHADOW_CLASS[settings.shadow] : ""} ${
                        settings.border ? BORDER_CLASS[settings.border] : ""}`}
                />
                {child}
            </div>
        );
    },
    serialize: (_child, settings: ImageSettings | undefined) => {
        const text = DOMPurify().sanitize(settings?.altText ?? "");
        return `<img src="${DOMPurify().sanitize(settings?.src ?? "")}" `
                    + `alt="${text}" width="${settings?.width?.toString() ?? ""}" `
                    + `height="${settings?.height?.toString() ?? ""}" `
                    + `shadow="${settings?.shadow?.toString() ?? ""}" `
                    + `border="${settings?.border?.toString() ?? ""}" `
                    + `align="${settings?.align?.toString() ?? ""}"`
                    + `>`;
    },
    deserialize: (children, settings) => jsx("element", {
        type: ElementType.IMAGE,
        [ElementType.IMAGE]: {
            src: settings?.getAttribute("src"),
            altText: settings?.getAttribute("alt"),
            height: settings?.getAttribute("height"),
            width: settings?.getAttribute("width"),
            shadow: settings?.getAttribute("shadow"),
            border: settings?.getAttribute("border"),
            align: settings?.getAttribute("align"),
        },
    }, children),

});
